import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import SchemaOrg from '../../components/SEO/SchemaOrg';

export default function Home() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    agree: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSpeakModal, setShowSpeakModal] = useState(false);
  const [speakingForm, setSpeakingForm] = useState({
    name: '',
    email: '',
    organization: '',
    eventType: '',
    eventDate: '',
    message: '',
    agree: false
  });
  const [isSpeakingSubmitting, setIsSpeakingSubmitting] = useState(false);
  const [speakingSubmitMessage, setSpeakingSubmitMessage] = useState('');
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    email: '',
    organization: '',
    partnershipType: '',
    industry: '',
    message: '',
    agree: false
  });
  const [isPartnerSubmitting, setIsPartnerSubmitting] = useState(false);
  const [partnerSubmitMessage, setPartnerSubmitMessage] = useState('');
  const [showLearnModal, setShowLearnModal] = useState(false);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [showBioModal, setShowBioModal] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<'ai' | 'clean-energy' | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Define awards array before using it
  const awards = [
    {
      title: 'Emy Africa, Woman of the Year, 2024',
      quote: "Valerie inspires us to envision and create Africa's industrial future.",
      author: 'Kojo Soboh',
      position: 'CEO, Emy Africa'
    },
    {
      title: '12th Class of the Harambean Entrepreneurial Alliance, Bretton Woods, 2019',
      quote:
        'Ms. Labi Harambean, embodies our mission; we are innovators scaling ventures to unlock the full potential of our people.',
      author: 'Okendo Lewis-Gayle',
      position: 'Founder, Harambeans'
    },
    {
      title: 'University of Colorado Boulder Mortenson Center Global Engineering Outstanding Professional Award, 2020',
      quote:
        'Recognized for exceptional work in sanitation marketing and global engineering solutions that improve lives across communities.',
      author: 'Mortenson Center',
      position: 'University of Colorado Boulder'
    },
    {
      title: 'Mandela Washington Fellowship for Young African Leaders, 2014',
      quote:
        'Selected by President Barack Obama for demonstrating exceptional leadership and commitment to transforming African communities.',
      author: 'Barack Obama',
      position: 'Former President of the United States'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % awards.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + awards.length) % awards.length);
  };

  // Auto-slide functionality
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % awards.length);
    }, 2000); // Change slide every 2 seconds

    return () => clearInterval(slideInterval);
  }, [awards.length]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnClick = () => {
    setShowLearnModal(true);
  };

  const handleReadMoreClick = (type: 'ai' | 'clean-energy') => {
    setSelectedInsight(type);
    setShowInsightModal(true);
  };

  const handleBioClick = () => {
    window.open('https://claude.ai/public/artifacts/71787730-b9c2-4fd1-94a1-9cbe09531190', '_blank');
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim() || !contactForm.agree) {
      setSubmitMessage('Please fill in all fields and agree to terms.');
      return;
    }

    if (contactForm.message.length > 500) {
      setSubmitMessage('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new FormData();
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('message', contactForm.message);

      const response = await fetch('https://gxvuxnbnvuqkvhdfgsku.supabase.co/functions/v1/collect-leads', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSubmitMessage('Message sent successfully! We will get back to you soon.');
        setContactForm({ name: '', email: '', message: '', agree: false });
      } else {
        setSubmitMessage('Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSpeakingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !speakingForm.name.trim() ||
      !speakingForm.email.trim() ||
      !speakingForm.organization.trim() ||
      !speakingForm.eventType.trim() ||
      !speakingForm.eventDate.trim() ||
      !speakingForm.message.trim() ||
      !speakingForm.agree
    ) {
      setSpeakingSubmitMessage('Please fill in all fields and agree to terms.');
      return;
    }

    if (speakingForm.message.length > 500) {
      setSpeakingSubmitMessage('Message must be 500 characters or less.');
      return;
    }

    setIsSpeakingSubmitting(true);
    setSpeakingSubmitMessage('');

    try {
      const formData = new FormData();
      formData.append('name', speakingForm.name);
      formData.append('email', speakingForm.email);
      formData.append('organization', speakingForm.organization);
      formData.append('eventType', speakingForm.eventType);
      formData.append('eventDate', speakingForm.eventDate);
      formData.append('message', speakingForm.message);

      const response = await fetch('https://gxvuxnbnvuqkvhdfgsku.supabase.co/functions/v1/collect-leads', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setSpeakingSubmitMessage('Speaking request sent successfully! We will get back to you soon.');
        setSpeakingForm({
          name: '',
          email: '',
          organization: '',
          eventType: '',
          eventDate: '',
          message: '',
          agree: false
        });
        setTimeout(() => {
          setShowSpeakModal(false);
          setSpeakingSubmitMessage('');
        }, 2000);
      } else {
        setSpeakingSubmitMessage('Failed to send request. Please try again.');
      }
    } catch (error) {
      setSpeakingSubmitMessage('Failed to send request. Please try again.');
    } finally {
      setIsSpeakingSubmitting(false);
    }
  };

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !partnerForm.name.trim() ||
      !partnerForm.email.trim() ||
      !partnerForm.organization.trim() ||
      !partnerForm.partnershipType.trim() ||
      !partnerForm.industry.trim() ||
      !partnerForm.message.trim() ||
      !partnerForm.agree
    ) {
      setPartnerSubmitMessage('Please fill in all fields and agree to terms.');
      return;
    }

    if (partnerForm.message.length > 500) {
      setPartnerSubmitMessage('Message must be 500 characters or less.');
      return;
    }

    setIsPartnerSubmitting(true);
    setPartnerSubmitMessage('');

    try {
      const formData = new FormData();
      formData.append('name', partnerForm.name);
      formData.append('email', partnerForm.email);
      formData.append('organization', partnerForm.organization);
      formData.append('partnershipType', partnerForm.partnershipType);
      formData.append('industry', partnerForm.industry);
      formData.append('message', partnerForm.message);

      const response = await fetch('https://gxvuxnbnvuqkvhdfgsku.supabase.co/functions/v1/collect-leads', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setPartnerSubmitMessage('Partnership request sent successfully! We will get back to you soon.');
        setPartnerForm({
          name: '',
          email: '',
          organization: '',
          partnershipType: '',
          industry: '',
          message: '',
          agree: false
        });
        setTimeout(() => {
          setShowPartnerModal(false);
          setPartnerSubmitMessage('');
        }, 2000);
      } else {
        setPartnerSubmitMessage('Failed to send request. Please try again.');
      }
    } catch (error) {
      setPartnerSubmitMessage('Failed to send request. Please try again.');
    } finally {
      setIsPartnerSubmitting(false);
    }
  };

  const services = [
    {
      title: 'Public Speaker',
      description:
        "As a keynote speaker, I share entrepreneurial visions that drive systemic change in Africa's manufacturing and innovation sectors.",
      icon: 'ri-mic-fill'
    },
    {
      title: 'Board Member',
      description:
        'With over a decade of experience on startup boards in Africa, I am committed to unlocking scalable, innovative solutions that enhance market value.',
      icon: 'ri-team-fill'
    },
    {
      title: 'Policy Advisor',
      description: 'I advocate for public‑private partnerships, offering strategic insights to African governments to enhance policy development.',
      icon: 'ri-government-fill'
    },
    {
      title: 'Venture Partner',
      description:
        'As a serial entrepreneur, I believe in empowering our community of entrepreneurs through mentorship and collaboration.',
      icon: 'ri-funds-fill'
    }
  ];

  const mediaArticles = [
    {
      category: 'Radio',
      title: "Eureka: Charging ahead with Ghana's first e-bike maker",
      readTime: '16 min listen',
      image:
        'https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/1bce80fa7475baef40e237f70bb69e10.webp',
      url: 'https://monocle.com/radio/shows/the-entrepreneurs/eureka-charging-ahead-with-ghanas-first-e-bike-maker/'
    },
    {
      category: 'Research',
      title: 'Women Leading Change: Valerie Labi',
      readTime: '8 min read',
      image:
        'https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/1b68bbfd301b3dc5dc7602a2f832247c.png',
      url: 'https://mapping-africa-transformations.org/women-leading-change-valerie-labi/'
    },
    {
      category: 'Industry',
      title: 'Perspectives: Powering New African Mobility, with Valerie Labi',
      readTime: '6 min read',
      image:
        'https://public.readdy.ai/ai/img_res/a8fc5a83634e14d45fd7dbc3144a2515.jpg',
      url: 'https://cargobikefestival.com/features/bycs-perspectives-powering-new-african-mobility-with-valerie-labi/'
    },
    {
      category: 'Interview',
      title:
        "Ebike entrepreneur Valeria Labi: 'If I see a problem and I think it can be solved, I follow that thread'",
      readTime: '8 min read',
      image:
        'https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/3b60bdc249984749ca2f90acf6e1297b.jfif',
      url: 'https://www.theguardian.com/business/article/2024/jul/30/ebike-entrepreneur-valeria-labi-if-i-see-a-problem-and-i-think-it-can-be-solved-i-follow-that-thread'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '"Inter", sans-serif' }}>
      {/* Schema.org JSON-LD */}
      <SchemaOrg type="Person" data={{
        name: 'Valerie Labi',
        jobTitle: 'Entrepreneur, Speaker, Climate Tech & AI Specialist',
        description: 'Engineering Africa\'s generational future through innovation, sustainable industry, and transformative leadership.',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        image: 'https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/d38fefeba9e460ab4200be0053c32c8b.png',
        sameAs: [
          'https://www.linkedin.com/in/valerie-labi-27217936/',
          'https://www.wahu.me'
        ],
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Accra',
          addressCountry: 'Ghana'
        },
        email: 'vlabi@hotmail.com',
        knowsAbout: [
          'African Entrepreneurship',
          'Climate Technology',
          'Artificial Intelligence',
          'Sustainable Innovation',
          'Clean Energy',
          'Electric Mobility',
          'Public Speaking',
          'Venture Capital'
        ],
        award: [
          'Emy Africa Woman of the Year 2024',
          'Harambean Entrepreneurial Alliance 2019',
          'University of Colorado Boulder Mortenson Center Global Engineering Outstanding Professional Award 2020',
          'Mandela Washington Fellowship for Young African Leaders 2014'
        ]
      }} />
      <SchemaOrg type="WebSite" data={{
        name: 'Valerie Labi',
        description: 'Engineering Africa\'s generational future through innovation, sustainable industry, and transformative leadership.',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        author: {
          '@type': 'Person',
          name: 'Valerie Labi'
        },
        publisher: {
          '@type': 'Person',
          name: 'Valerie Labi'
        }
      }} />
      
      <Header />

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/bc7decd23b0477a91c44580758c00694.jfif')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-2xl bg-black/30 backdrop-blur-sm rounded-2xl p-6 lg:p-8 mt-64 md:mt-0">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Valerie Labi — Entrepreneur, Speaker, Climate Tech & AI Specialist
            </h1>
            <p className="text-lg lg:text-xl text-gray-200 mb-6 leading-relaxed">
              Engineering Africa's generational future through innovation, sustainable industry,
              transformative leadership. Driving economic self‑determination across the continent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-7s0" onClick={() => setShowSpeakModal(true)}>
                <i className="ri-mic-fill mr-2"></i>
                Speak
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-gray-900"
                onClick={() => setShowPartnerModal(true)}
              >
                <i className="ri-handshake-fill mr-2"></i>
                Partner
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Learn Modal */}
      {showLearnModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900">Africa's Industrial Future</h3>
                <button onClick={() => setShowLearnModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <img
                    src="https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/33ef15bec24d0f2f1df93aeeffe08e66.png"
                    alt="Africa's Industrial Future"
                    className="rounded-xl shadow-lg object-cover w-full h-64 mb-6"
                  />

                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Vision for Transformation</h4>
                  <p className="text-gray-600 mb-4">
                    Africa stands at the threshold of an unprecedented industrial revolution. Through strategic
                    innovation in clean energy, manufacturing, and technology, we can create sustainable economic growth
                    that benefits all Africans.
                  </p>

                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Focus Areas</h4>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Electric mobility and sustainable transportation solutions</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Clean energy infrastructure and renewable power systems</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>AI‑driven manufacturing and industrial automation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                      <span>Youth empowerment through technical education and entrepreneurship</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">The Path Forward</h4>
                  <p className="text-gray-600 mb-6">
                    My approach combines proven business strategies with innovative technologies to create scalable
                    solutions that address Africa's unique challenges while building competitive advantages in global
                    markets.
                  </p>

                  <div className="bg-teal-50 p-6 rounded-xl mb-6">
                    <h5 className="text-lg font-semibold text-teal-900 mb-3">Impact Goals by 2030</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-teal-600">100K+</div>
                        <div className="text-sm text-teal-800">Jobs Created</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-teal-600">50+</div>
                        <div className="text-sm text-teal-800">Ventures Supported</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-teal-600">20+</div>
                        <div className="text-sm text-teal-800">Countries Impacted</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-teal-600">1M+</div>
                        <div className="text-sm text-teal-800">Lives Improved</div>
                      </div>
                    </div>
                  </div>

                  <blockquote className="text-lg text-gray-700 italic border-l-4 border-teal-600 pl-4 mb-6">
                    "The Africa our generation desires can be won. It exists, it is real, it is possible. It is ours."
                  </blockquote>

                  <div className="flex gap-4">
                    <Button onClick={() => scrollToSection('services')}>Explore Services</Button>
                    <Button variant="outline" onClick={() => scrollToSection('contact')}>
                      Get Involved
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bio Modal */}
      {showBioModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900">My Bio: Valerie Labi</h3>
                <button onClick={() => setShowBioModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <img
                    src="https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/d38fefeba9e460ab4200be0053c32c8b.png"
                    alt="Valerie Labi - Co-Founder & CEO, Wahu Mobility"
                    className="rounded-xl shadow-lg object-cover w-full h-80 mb-6 object-top"
                  />

                  <div className="bg-teal-50 p-6 rounded-xl">
                    <h4 className="text-xl font-semibold text-teal-900 mb-3">Co-Founder & CEO, Wahu Mobility</h4>
                    <p className="text-teal-800 text-sm mb-4">
                      Leading Ghana's first electric vehicle manufacturing plant, launched in Accra in February 2023.
                    </p>
                    <div className="flex items-center text-sm text-teal-700">
                      <i className="ri-external-link-line mr-2"></i>
                      <a href="https://www.wahu.me" target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">
                        Visit Wahu Mobility
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Visionary Impact Entrepreneur</h4>
                    <p className="text-gray-600 mb-4">
                      Valerie Labi is a visionary serial impact entrepreneur committed to advancing the United Nations 
                      Sustainable Development Goals through innovative social enterprises. Under her leadership, Wahu 
                      Mobility is building a robust e-mobility ecosystem across the continent, projected to generate 
                      over 100,000 climate-smart jobs for young Africans by 2030.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Redefining Sustainable Transportation</h4>
                    <p className="text-gray-600 mb-4">
                      Wahu Mobility is redefining sustainable transportation in Africa through its fleet-as-a-service 
                      digital platform, offering subscription-based EV ownership, income-generating opportunities, 
                      carbon-backed loyalty programs, and tailored EV insurance. The company's flagship eBike is 
                      already operational, with a new line of made-in-Ghana electric vehicles launching later in 2025.
                    </p>
                  </div>

                  <div className="bg-green-50 p-6 rounded-xl">
                    <h4 className="text-lg font-semibold text-green-900 mb-3">Historic Climate Achievement</h4>
                    <p className="text-green-800 text-sm">
                      In a landmark achievement, Valerie pioneered Wahu Mobility's authorization to sell carbon credits 
                      under Article 6 of the Paris Agreement, making it only the second e-mobility company globally to 
                      receive this designation. The historic deal with Switzerland underscores the global relevance of 
                      Africa-led climate solutions.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Education & Experience</h4>
                    <div className="space-y-3 text-gray-600">
                      <div className="flex items-start">
                        <i className="ri-graduation-cap-line text-teal-600 mr-2 mt-1"></i>
                        <span>BSc in Economics from the University of Southampton</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-graduation-cap-line text-teal-600 mr-2 mt-1"></i>
                        <span>Master's in Sustainability Leadership from the University of Cambridge</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-briefcase-line text-teal-600 mr-2 mt-1"></i>
                        <span>Leadership roles at Vodafone and EY</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Recognition & Awards</h4>
                    <div className="space-y-3 text-gray-600">
                      <div className="flex items-start">
                        <i className="ri-award-line text-teal-600 mr-2 mt-1"></i>
                        <span>Mandela Washington Fellowship awarded by President Barack Obama (2014)</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-award-line text-teal-600 mr-2 mt-1"></i>
                        <span>Global Engineering Outstanding Professional Award (2020)</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-award-line text-teal-600 mr-2 mt-1"></i>
                        <span>Emy Africa Woman of the Year award (2024)</span>
                      </div>
                      <div className="flex items-start">
                        <i className="ri-team-line text-teal-600 mr-2 mt-1"></i>
                        <span>Member of the Harambean entrepreneurial alliance</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Social Industrialist</h4>
                    <p className="text-gray-600 text-sm italic">
                      "Valerie identifies as a social industrialist, shaping a more sustainable, inclusive, and 
                      prosperous future for Africa and the world."
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Button onClick={() => scrollToSection('contact')}>Connect with Valerie</Button>
                <Button variant="outline" onClick={() => setShowBioModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insight Modal */}
      {showInsightModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-gray-900">
                  {selectedInsight === 'ai' ? 'AI and African Entrepreneurship' : 'Clean Energy Ecosystems'}
                </h3>
                <button onClick={() => setShowInsightModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {selectedInsight === 'ai' ? (
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=African%20entrepreneurs%20working%20with%20AI%20technology%2C%20modern%20tech%20hub%20in%20Africa%2C%20diverse%20team%20collaborating%20on%20artificial%20intelligence%20projects%2C%20innovation%20and%20technology%20theme%2C%20professional%20photography&width=800&height=400&seq=ai1&orientation=landscape"
                    alt="AI and African Entrepreneurship"
                    className="rounded-xl shadow-lg object-cover w-full h-64 mb-6"
                  />

                  <div className="prose max-w-none">
                    <p className="text-lg text-gray-600 mb-6">
                      Artificial Intelligence represents a transformative opportunity for African entrepreneurship,
                      offering unprecedented potential to leapfrog traditional development challenges and create
                      innovative solutions tailored to our continent's unique needs.
                    </p>

                    <h4 className="text-xl font-semibold text-gray-900 mb-4">The AI Advantage in Africa</h4>
                    <p className="text-gray-600 mb-4">
                      Africa's young, tech‑savvy population and diverse market challenges create the perfect environment
                      for AI innovation. From fintech solutions that serve the unbanked to agricultural technologies that
                      optimize crop yields, AI is enabling African entrepreneurs to build solutions that scale across the
                      continent.
                    </p>

                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Applications</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-blue-900 mb-2">Healthcare AI</h5>
                        <p className="text-blue-800 text-sm">
                          Diagnostic tools and telemedicine platforms expanding healthcare access across rural communities.
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-green-900 mb-2">Agricultural Intelligence</h5>
                        <p className="text-green-800 text-sm">
                          Precision farming and crop monitoring systems optimizing food production and sustainability.
                        </p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-purple-900 mb-2">Financial Services</h5>
                        <p className="text-purple-800 text-sm">
                          AI‑powered credit scoring and mobile banking solutions serving previously excluded populations.
                        </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-orange-900 mb-2">Education Technology</h5>
                        <p className="text-orange-800 text-sm">
                          Personalized learning platforms and language processing tools enhancing educational outcomes.
                        </p>
                      </div>
                    </div>

                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Building the Future</h4>
                    <p className="text-gray-600 mb-4">
                      Success in AI requires more than just technology—it demands understanding of local contexts,
                      ethical considerations, and sustainable business models. African entrepreneurs are uniquely
                      positioned to create AI solutions that are both globally competitive and locally relevant.
                    </p>

                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h5 className="text-lg font-semibold text-gray-900 mb-3">Next Steps for Entrepreneurs</h5>
                      <ul className="space-y-2 text-gray-600">
                        <li>• Identify specific local problems that AI can solve effectively</li>
                        <li>• Build diverse teams combining technical and domain expertise</li>
                        <li>• Focus on data quality and ethical AI development practices</li>
                        <li>• Develop partnerships with international tech companies and research institutions</li>
                        <li>• Create sustainable business models that can scale across African markets</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Solar%20panels%20and%20wind%20turbines%20in%20African%20landscape%2C%20clean%20energy%20infrastructure%2C%20sustainable%20power%20generation%2C%20green%20technology%20in%20Africa%2C%20renewable%20energy%20systems%2C%20professional%20industrial%20photography&width=800&height=400&seq=clean1&orientation=landscape"
                    alt="Clean Energy Ecosystems"
                    className="rounded-xl shadow-lg object-cover w-full h-64 mb-6"
                  />

                  <div className="prose max-w-none">
                    <p className="text-lg text-gray-600 mb-6">
                      Clean energy ecosystems represent the foundation of Africa's sustainable industrial future.
                      By building integrated renewable energy systems, we can power economic growth while addressing
                      climate challenges and creating energy independence.
                    </p>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">The Clean Energy Opportunity</h4>
                    <p className="text-gray-600 mb-4">
                      Africa possesses abundant renewable energy resources—from solar potential in the Sahara to
                      hydroelectric opportunities along major rivers. The challenge lies in building integrated systems
                      that can reliably power industrial development while remaining economically viable.
                    </p>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Ecosystem Components</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-yellow-900 mb-2">Solar Infrastructure</h5>
                        <p className="text-yellow-800 text-sm">
                          Distributed solar systems and utility‑scale installations providing reliable, cost‑effective power generation.
                        </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-blue-900 mb-2">Energy Storage</h5>
                        <p className="text-blue-800 text-sm">
                          Battery systems and grid stabilization technologies ensuring consistent power supply for industrial operations.
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-green-900 mb-2">Smart Grids</h5>
                        <p className="text-green-800 text-sm">
                          Intelligent distribution networks optimizing energy flow and enabling peer‑to‑peer energy trading.
                        </p>
                      </div>
                      <div className="bg-teal-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-teal-900 mb-2">Electric Mobility</h5>
                        <p className="text-teal-800 text-sm">
                          Transportation electrification creating demand for clean energy while reducing emissions.
                        </p>
                      </div>
                    </div>

                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Strategic Implementation</h4>
                    <p className="text-gray-600 mb-4">
                      Building successful clean energy ecosystems requires coordinated efforts across policy, finance,
                      and technology. We focus on creating bankable projects that attract international investment while
                      building local capacity and ownership.
                    </p>

                    <div className="bg-gray-50 p-6 rounded-xl">
                      <h5 className="text-lg font-semibold text-gray-900 mb-3">Development Priorities</h5>
                      <ul className="space-y-2 text-gray-600">
                        <li>• Establish regulatory frameworks that encourage private investment</li>
                        <li>• Develop local manufacturing capabilities for renewable energy components</li>
                        <li>• Create financing mechanisms accessible to African entrepreneurs</li>
                        <li>• Build technical education programs for clean‑energy careers</li>
                        <li>• Foster regional cooperation for cross‑border energy trading</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 mt-8">
                <Button onClick={() => scrollToSection('contact')}>Discuss Opportunities</Button>
                <Button variant="outline" onClick={() => setShowInsightModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Speaking Request Modal */}
      {showSpeakModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Speaking Request</h3>
                <button
                  onClick={() => setShowSpeakModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Request Valerie Labi as a keynote speaker for your event. Share your event details and we'll get back to you soon.
              </p>

              <form onSubmit={handleSpeakingSubmit} data-readdy-form id="speaking-request">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="speak-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="speak-name"
                      name="name"
                      value={speakingForm.name}
                      onChange={(e) => setSpeakingForm({ ...speakingForm, name: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="speak-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="speak-email"
                      name="email"
                      value={speakingForm.email}
                      onChange={(e) => setSpeakingForm({ ...speakingForm, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                      Organization *
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={speakingForm.organization}
                      onChange={(e) => setSpeakingForm({ ...speakingForm, organization: e.target.value })}
                      placeholder="Company or organization name"
                      className="w-100 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type *
                    </label>
                    <div className="relative">
                      <select
                        id="eventType"
                        name="eventType"
                        value={speakingForm.eventType}
                        onChange={(e) => setSpeakingForm({ ...speakingForm, eventType: e.target.value })}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                        required
                      >
                        <option value="">Select event type</option>
                        <option value="Conference">Conference</option>
                        <option value="Summit">Summit</option>
                        <option value="Workshop">Workshop</option>
                        <option value="Panel Discussion">Panel Discussion</option>
                        <option value="Corporate Event">Corporate Event</option>
                        <option value="University Event">University Event</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={speakingForm.eventDate}
                    onChange={(e) => setSpeakingForm({ ...speakingForm, eventDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={speakingForm.message}
                    onChange={(e) => setSpeakingForm({ ...speakingForm, message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={speakingForm.agree}
                      onChange={(e) => setSpeakingForm({ ...speakingForm, agree: e.target.checked })}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-600">I agree to the terms and conditions</span>
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" size="lg" className="flex-1" disabled={isSpeakingSubmitting}>
                    {isSpeakingSubmitting ? 'Sending Request...' : 'Send Speaking Request'}
                  </Button>
                  <Button type="button" variant="outline" size="lg" onClick={() => setShowSpeakModal(false)}>
                    Cancel
                  </Button>
                </div>

                {speakingSubmitMessage && (
                  <p
                    className={`mt-4 text-sm text-center ${
                      speakingSubmitMessage.toLowerCase().includes('success')
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {speakingSubmitMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Partnership Request Modal */}
      {showPartnerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-height-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Partnership Request</h3>
                <button
                  onClick={() => setShowPartnerModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Interested in partnering? Provide your details below and we'll discuss collaborative opportunities.
              </p>

              <form onSubmit={handlePartnerSubmit} data-readdy-form id="partner-request">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="partner-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="partner-name"
                      name="name"
                      value={partnerForm.name}
                      onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="partner-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="partner-email"
                      name="email"
                      value={partnerForm.email}
                      onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="partner-organization" className="block text-sm font-medium text-gray-700 mb-2">
                      Organization *
                    </label>
                    <input
                      type="text"
                      id="partner-organization"
                      name="organization"
                      value={partnerForm.organization}
                      onChange={(e) => setPartnerForm({ ...partnerForm, organization: e.target.value })}
                      placeholder="Company or organization name"
                      className="w-100 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="partnershipType" className="block text-sm font-medium text-gray-700 mb-2">
                      Partnership Type *
                    </label>
                    <select
                      id="partnershipType"
                      name="partnershipType"
                      value={partnerForm.partnershipType}
                      onChange={(e) => setPartnerForm({ ...partnerForm, partnershipType: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    >
                      <option value="">Select type</option>
                      <option value="Technology">Technology</option>
                      <option value="Investment">Investment</option>
                      <option value="Co‑creation">Co‑creation</option>
                      <option value="Research">Research</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <input
                      type="text"
                      id="industry"
                      name="industry"
                      value={partnerForm.industry}
                      onChange={(e) => setPartnerForm({ ...partnerForm, industry: e.target.value })}
                      placeholder="e.g., Clean Energy, Mobility"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="partner-message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="partner-message"
                      name="message"
                      rows={4}
                      value={partnerForm.message}
                      onChange={(e) => {
                        setPartnerForm({ ...partnerForm, message: e.target.value });
                      }}
                      placeholder="Brief description of partnership interest"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    ></textarea>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={partnerForm.agree}
                      onChange={(e) => setPartnerForm({ ...partnerForm, agree: e.target.checked })}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-600">I agree to the terms and conditions</span>
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" size="lg" className="flex-1" disabled={isPartnerSubmitting}>
                    {isPartnerSubmitting ? 'Sending Request...' : 'Send Partnership Request'}
                  </Button>
                  <Button type="button" variant="outline" size="lg" onClick={() => setShowPartnerModal(false)}>
                    Cancel
                  </Button>
                </div>

                {partnerSubmitMessage && (
                  <p
                    className={`mt-4 text-sm text-center ${
                      partnerSubmitMessage.toLowerCase().includes('success')
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {partnerSubmitMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Vision Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Engineering Africa's Generational Future</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                I believe in Africa's potential to lead the world in sustainable innovation. Through strategic
                entrepreneurship, clean technology, and transformative leadership, we can build an Africa that works
                for all Africans.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                My work focuses on creating scalable solutions that address our continent's unique challenges while
                building competitive advantages in global markets. From electric mobility to AI-driven manufacturing,
                I'm committed to unlocking Africa's industrial potential.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={handleLearnClick}>
                  <i className="ri-lightbulb-line mr-2"></i>
                  Learn More
                </Button>
                <Button size="lg" variant="outline" onClick={handleBioClick}>
                  <i className="ri-user-line mr-2"></i>
                  My Bio
                </Button>
                <a
                  href="https://www.wahu.me"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-building-line mr-2"></i>
                  My Ventures
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/d38fefeba9e460ab4200be0053c32c8b.png"
                alt="Valerie Labi - Entrepreneur and Innovation Leader"
                className="rounded-2xl shadow-2xl object-cover w-full h-96 lg:h-[500px] object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Driving Systemic Change</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Creating measurable impact across Africa through innovation, entrepreneurship, and strategic partnerships
            </p>
          </div>

          {/* Updated Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-briefcase-fill text-2xl text-teal-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">300+</div>
              <div className="text-gray-600">Jobs Created</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-building-fill text-2xl text-blue-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">5+</div>
              <div className="text-gray-600">Ventures Mentoring</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-global-fill text-2xl text-green-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">4+</div>
              <div className="text-gray-600">Countries Reached</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg mb-6">
                <i className="ri-cpu-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI & African Entrepreneurship</h3>
              <p className="text-gray-600 mb-6">
                Leveraging artificial intelligence to solve uniquely African challenges, from healthcare access to
                agricultural optimization, while building global tech ecosystems.
              </p>
              <button
                onClick={() => handleReadMoreClick('ai')}
                className="text-teal-600 font-semibold hover:text-teal-7p0 transition-colors cursor-pointer"
              >
                Read More →
              </button>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-lg mb-6">
                <i className="ri-leaf-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Clean Energy Ecosystems</h3>
              <p className="text-gray-600 mb-6">
                Building integrated renewable energy systems that power industrial growth while creating sustainable
                economic opportunities across the continent.
              </p>
              <button
                onClick={() => handleReadMoreClick('clean-energy')}
                className="text-green-600 font-semibold hover:text-green-7p0 transition-colors cursor-pointer"
              >
                Read More →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How I Can Help</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bringing decades of entrepreneurial experience and strategic insight to drive transformative change
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
                  <i className={`${service.icon} text-xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" onClick={() => setShowSpeakModal(true)}>
              <i className="ri-calendar-line mr-2"></i>
              Book a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Awards &amp; Recognition</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recognized globally for transformative leadership and innovation in African entrepreneurship
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Photo Card - Now on the left */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
                <img
                  src="https://static.readdy.ai/image/67154b7c41a7c2d344a7e35d4f6022a4/8c0ff248e6365e5705e59a9a7974f3ce.png"
                  alt="Valerie Labi receiving award in green dress"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Awards Carousel - Now on the right */}
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {awards.map((award, index) => (
                      <div key={index} className="w-full flex-shrink-0 px-4">
                        <div className="bg-white rounded-2xl shadow-lg p-8">
                          <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">{award.title}</h3>
                            <blockquote className="text-lg text-gray-700 italic mb-6 leading-relaxed">
                              &quot;{award.quote}&quot;
                            </blockquote>
                            <div className="flex items-center justify-center">
                              <div>
                                <p className="font-semibold text-gray-900">{award.author}</p>
                                <p className="text-gray-600">{award.position}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 transform translate-y-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl text-gray-600"></i>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transform translate-y-4 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-xl text-gray-600"></i>
                </button>

                {/* Dots Indicator */}
                <div className="flex justify-center mt-12 space-x-2">
                  {awards.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors cursor-pointer ${
                        index === currentSlide ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Events</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Speaking engagements and thought leadership at premier global forums
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Past Events */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-calendar-check-line text-teal-600 mr-3"></i>
                Past Events
              </h3>

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Oxford Africa Business Forum</h4>
                      <p className="text-gray-600 mb-3">Keynote speaker on African entrepreneurship and innovation ecosystems.</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-map-pin-line mr-2"></i>
                        Oxford University, UK
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">African Investment Forum</h4>
                      <p className="text-gray-600 mb-3">Panel discussion on local manufacturing and clean energy investments.</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-map-pin-line mr-2"></i>
                        Casablanca, Morocco
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">EBRD Annual Meeting</h4>
                      <p className="text-gray-600 mb-3">
                        Speaking on private sector development and technological transformation in Africa.
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-map-pin-line mr-2"></i>
                        London, UK
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">IATF2025</h4>
                      <p className="text-gray-600 mb-3">
                        Intra‑African Trade Fair presentation on manufacturing and industrial development.
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-map-pin-line mr-2"></i>
                        Cairo, Egypt
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Completed
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Events */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-calendar-event-line text-teal-600 mr-3"></i>
                Upcoming Events
              </h3>

              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-teal-600"></div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Africa eMobility Week Ethiopia</h4>
                      <p className="text-gray-600 mb-3">Leading discussions on electric mobility solutions and sustainable transportation across the continent.</p>
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <i className="ri-map-pin-line mr-2"></i>
                        Addis Ababa, Ethiopia
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-calendar-line mr-2"></i>
                        Coming Soon
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Upcoming
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center text-sm text-teal-600">
                      <i className="ri-mic-line mr-2"></i>
                      Keynote Speaker
                    </div>
                    <a
                      href="https://africaemobilityweek.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                    >
                      Learn More →
                    </a>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-teal-600"></div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">CoMotion Riyadh</h4>
                      <p className="text-gray-600 mb-3">Presenting on the future of mobility and transportation innovation in emerging markets.</p>
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <i className="ri-map-pin-line mr-2"></i>
                        Riyadh, Saudi Arabia
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-calendar-line mr-2"></i>
                        Coming Soon
                      </div>
                    </div>
                    <div className="ml-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Upcoming
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center text-sm text-teal-600">
                      <i className="ri-presentation-line mr-2"></i>
                      Featured Speaker
                    </div>
                    <a
                      href="https://www.comotionglobal.com/comotionglobal2025"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                    >
                      Learn More →
                    </a>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-100">
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Interested in Having Valerie Speak?</h4>
                    <p className="text-gray-600 mb-4">
                      Book Valerie for your next event to share insights on African entrepreneurship and innovation
                    </p>
                    <Button onClick={() => setShowSpeakModal(true)} className="bg-teal-600 hover:bg-teal-700">
                      <i className="ri-mic-fill mr-2"></i>
                      Request Speaking Engagement
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Section */}
      <section id="media" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Media &amp; Publications</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Featured insights and thought leadership on African innovation and entrepreneurship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mediaArticles.map((article, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                      {article.category}
                    </span>
                    <span className="text-sm text-gray-500">{article.readTime}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 leading-tight">{article.title}</h3>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
                  >
                    Read More
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Interested in featuring Valerie's insights in your publication?
            </p>
            <Button size="lg" onClick={() => scrollToSection('contact')}>
              <i className="ri-mail-line mr-2"></i>
              Media Inquiries
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to collaborate on transformative projects? Let's discuss how we can work together to drive change.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Let's Connect</h3>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're interested in speaking engagements, partnerships, or strategic consulting, I'd love to hear from you.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mr-4">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-gray-600">vlabi@hotmail.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mr-4">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Location</h4>
                    <p className="text-gray-600">Accra, Ghana</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Follow My Journey</h4>
                <div className="flex gap-4">
                  <a href="https://www.linkedin.com/in/valerie-labi-27217936/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-gray-100 hover:bg-teal-600 hover:text-white rounded-lg transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h3>

              <form onSubmit={handleContactSubmit} data-readdy-form id="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    maxLength={500}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">{contactForm.message.length}/500 characters</p>
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={contactForm.agree}
                      onChange={(e) => setContactForm({ ...contactForm, agree: e.target.checked })}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      required
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      I agree to the terms and conditions and privacy policy
                    </span>
                  </label>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2"></i>
                      Send Message
                    </>
                  )}
                </Button>

                {submitMessage && (
                  <p
                    className={`mt-4 text-sm text-center ${
                      submitMessage.toLowerCase().includes('success')
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {submitMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
